import React, { useState, useEffect } from 'react';

export default function Phase10TVDisplay() {
  const phaseDescriptions = [
    '2 sets of 3',
    '1 set of 3 + 1 run of 4',
    '1 set of 4 + 1 run of 4',
    '1 run of 7',
    '1 run of 8',
    '1 run of 9',
    '2 sets of 4',
    '7 cards of one color',
    '1 set of 5 + 1 set of 2',
    '1 set of 5 + 1 set of 3'
  ];

  const [playerCount, setPlayerCount] = useState(3);
  const [players, setPlayers] = useState(['Player 1', 'Player 2', 'Player 3']);
  const [rounds, setRounds] = useState(10);
  const [scores, setScores] = useState([Array(10).fill(''), Array(10).fill(''), Array(10).fill('')]);
  const [phaseOverrides, setPhaseOverrides] = useState({});
  const [winner, setWinner] = useState(null);

  // Load and sync data from localStorage
  useEffect(() => {
    const loadData = () => {
      try {
        const stored = localStorage.getItem('phase10-game-v3');
        if (stored) {
          const data = JSON.parse(stored);
          if (data.players) setPlayers(data.players);
          if (data.scores) setScores(data.scores);
          if (data.rounds) setRounds(data.rounds);
          if (data.phaseOverrides) setPhaseOverrides(data.phaseOverrides);
          if (data.playerCount) setPlayerCount(data.playerCount);
        }
      } catch (err) {
        console.log('Error loading data:', err);
      }
    };

    // Initial load
    loadData();

    // Listen for storage changes (real-time sync)
    const handleStorageChange = (e) => {
      if (e.key === 'phase10-game-v3') {
        loadData();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also poll for changes (backup for same-tab updates)
    const interval = setInterval(loadData, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const calculatePhase = (playerIndex) => {
    if (phaseOverrides[playerIndex] !== undefined) {
      return phaseOverrides[playerIndex];
    }

    let phase = 1;
    for (let r = 0; r < (scores[playerIndex]?.length || 0); r++) {
      const score = parseInt(scores[playerIndex]?.[r]);
      if (!isNaN(score) && score < 50) {
        phase = Math.min(10, phase + 1);
      }
    }
    return phase;
  };

  const getCurrentPhases = () => {
    return players.slice(0, playerCount).map((_, i) => calculatePhase(i));
  };

  // Check for winner
  useEffect(() => {
    let foundWinner = null;

    players.slice(0, playerCount).forEach((_, pi) => {
      let completedPhases = 0;
      for (const s of (scores[pi] || [])) {
        const score = parseInt(s);
        if (!isNaN(score) && score < 50) completedPhases++;
      }

      if (completedPhases >= 10) {
        foundWinner = pi;
      }
    });

    setWinner(foundWinner);
  }, [scores, phaseOverrides, playerCount, players]);

  const getTotal = (playerIndex) => {
    return (scores[playerIndex] || []).reduce((sum, s) => sum + (parseInt(s) || 0), 0);
  };

  const getMedal = (rank) => ['🥇', '🥈', '🥉'][rank] || `#${rank + 1}`;

  const currentPhases = getCurrentPhases();
  const currentRound = Math.max(0, (scores[0] || []).findIndex(s => s === ''));
  const currentDealer = currentRound % playerCount;

  const leaderboard = players.slice(0, playerCount).map((p, i) => ({
    name: p,
    index: i,
    total: getTotal(i),
    phase: currentPhases[i]
  })).filter(p => p.total > 0 || p.phase > 1).sort((a, b) => {
    if (b.phase !== a.phase) return b.phase - a.phase;
    return a.total - b.total;
  });

  // Full player list for display (unsorted)
  const allPlayers = players.slice(0, playerCount).map((p, i) => ({
    name: p,
    index: i,
    total: getTotal(i),
    phase: currentPhases[i]
  }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-cyan-900 p-8 flex">
      {/* Main Scorecard - Left Side (70%) */}
      <div className="flex-1 pr-6 flex flex-col">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-6xl font-bold text-cyan-300 mb-2">🎯 Phase 10 🎯</h1>
          <p className="text-blue-300 text-2xl">
            {winner !== null ? '🎉 Game Complete!' : `Round ${currentRound + 1} of ${rounds} • 🃏 ${players[currentDealer]} shuffling`}
          </p>
        </div>

        {/* Winner Banner */}
        {winner !== null && (
          <div className="bg-gradient-to-r from-yellow-500 via-amber-400 to-yellow-500 rounded-2xl p-6 mb-6 text-center animate-pulse">
            <div className="text-5xl mb-2">🏆🎉🏆</div>
            <div className="text-blue-950 font-bold text-4xl">{players[winner]} WINS!</div>
            <div className="text-blue-900 text-2xl">Completed all 10 phases with {getTotal(winner)} points</div>
          </div>
        )}

        {/* Phase Display Row */}
        <div className="bg-blue-800/50 rounded-xl p-4 mb-4 border border-blue-600/30">
          <div className="flex justify-center gap-4 flex-wrap">
            {players.slice(0, playerCount).map((player, pi) => (
              <div
                key={pi}
                className={`flex flex-col items-center rounded-xl p-4 min-w-[120px] ${
                  currentDealer === pi ? 'bg-amber-500/30 border-2 border-amber-400/50' :
                  winner === pi ? 'bg-yellow-500/30 border-2 border-yellow-400' :
                  leaderboard[0]?.index === pi ? 'bg-cyan-500/20 border border-cyan-400/30' :
                  'bg-blue-900/50'
                }`}
              >
                <span className="text-lg text-blue-200 font-medium truncate max-w-[100px]">
                  {currentDealer === pi ? '🃏 ' : ''}{winner === pi ? '🏆 ' : ''}{player}
                </span>
                <span className="text-4xl font-bold text-cyan-300 mt-1">P{currentPhases[pi]}</span>
                <span className="text-sm text-blue-300 mt-1">{phaseDescriptions[currentPhases[pi] - 1]}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Score Table */}
        <div className="flex-1 overflow-auto rounded-2xl bg-blue-900/50 border border-blue-700/50">
          <table className="text-xl border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-blue-800">
                <th className="p-4 text-cyan-300 text-2xl font-bold text-left w-20">Rnd</th>
                {players.slice(0, playerCount).map((player, i) => (
                  <th
                    key={i}
                    className={`p-4 text-center min-w-[120px] max-w-[180px] transition-all ${
                      currentDealer === i ? 'bg-amber-500/30' : ''
                    } ${winner === i ? 'bg-yellow-500/30' : ''}`}
                  >
                    <span className={`text-xl font-bold ${
                      currentDealer === i ? 'text-amber-300' :
                      winner === i ? 'text-yellow-300' :
                      leaderboard[0]?.index === i ? 'text-cyan-300' : 'text-white'
                    }`}>
                      {currentDealer === i ? '🃏 ' : ''}{winner === i ? '🏆 ' : ''}{player}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rounds }, (_, ri) => {
                const roundDealer = ri % playerCount;
                return (
                  <tr
                    key={ri}
                    className={`border-t border-blue-700/30 ${
                      ri === currentRound ? 'bg-blue-700/30' : ''
                    }`}
                  >
                    <td className={`p-4 font-bold text-xl text-left ${
                      ri === currentRound ? 'text-cyan-200 bg-blue-800/50' : 'text-cyan-400'
                    }`}>
                      {ri === currentRound ? '▶ ' : ''}{ri + 1}
                    </td>
                    {players.slice(0, playerCount).map((_, pi) => {
                      const score = parseInt(scores[pi]?.[ri]);
                      const completed = !isNaN(score) && score < 50;
                      const isDealer = roundDealer === pi;
                      return (
                        <td
                          key={pi}
                          className={`p-4 text-center text-2xl font-mono ${
                            isDealer && ri === currentRound ? 'bg-amber-500/10' : ''
                          }`}
                        >
                          <span className={completed ? 'text-green-400 font-bold' : 'text-white'}>
                            {scores[pi]?.[ri] || '–'}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {/* Totals Row */}
              <tr className="bg-blue-950 font-bold border-t-4 border-blue-600 sticky bottom-0">
                <td className="p-4 text-cyan-400 text-2xl">TOT</td>
                {players.slice(0, playerCount).map((_, pi) => {
                  const rank = leaderboard.findIndex(p => p.index === pi);
                  return (
                    <td
                      key={pi}
                      className={`p-4 text-center text-3xl ${
                        winner === pi ? 'text-yellow-300' :
                        rank === 0 && leaderboard.length > 0 ? 'text-cyan-300' : 'text-white'
                      }`}
                    >
                      {getTotal(pi)}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Leaderboard - Right Side (30%) */}
      <div className="w-96 flex flex-col">
        <div className="bg-blue-800/50 rounded-2xl p-6 border border-blue-600/30 flex-1 flex flex-col">
          <h2 className="text-4xl font-bold text-cyan-300 mb-6 text-center">🏆 LEADERBOARD</h2>

          {/* Top 3 Podium - Large */}
          <div className="space-y-4 mb-6">
            {(leaderboard.length > 0 ? leaderboard : allPlayers).slice(0, 3).map((player, i) => {
              const rank = leaderboard.findIndex(p => p.index === player.index);
              const displayRank = rank >= 0 ? rank : i;
              const isWinner = winner === player.index;
              const hasData = leaderboard.length > 0;

              return (
                <div
                  key={player.index}
                  className={`flex items-center justify-between px-6 py-5 rounded-xl transition-all ${
                    !hasData ? 'bg-blue-900/30' :
                    isWinner ? 'bg-yellow-500/40 border-2 border-yellow-400 animate-pulse' :
                    displayRank === 0 ? 'bg-cyan-500/30 border border-cyan-400/50' :
                    displayRank === 1 ? 'bg-gray-400/20 border border-gray-400/30' :
                    displayRank === 2 ? 'bg-orange-600/20 border border-orange-500/30' :
                    'bg-blue-900/30'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-5xl">
                      {!hasData ? `${i + 1}.` : isWinner ? '🏆' : getMedal(displayRank)}
                    </span>
                    <div className="flex flex-col">
                      <span className={`text-2xl font-bold ${
                        displayRank === 0 && hasData ? 'text-cyan-300' : 'text-white'
                      }`}>
                        {player.name}
                      </span>
                      <span className="text-lg text-blue-300">Phase {player.phase}</span>
                    </div>
                  </div>
                  <span className={`text-3xl font-bold font-mono ${
                    displayRank === 0 && hasData ? 'text-cyan-300' : 'text-white'
                  }`}>
                    {hasData ? player.total : '–'}
                  </span>
                </div>
              );
            })}
          </div>

          {/* 4th place and beyond - Scrollable, smaller */}
          {leaderboard.length > 3 && (
            <div className="flex-1 overflow-auto">
              <div className="space-y-2">
                {leaderboard.slice(3).map((player, i) => (
                  <div
                    key={player.index}
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-blue-900/30"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl text-blue-400">#{i + 4}</span>
                      <div className="flex flex-col">
                        <span className="text-lg text-white">{player.name}</span>
                        <span className="text-sm text-blue-400">Phase {player.phase}</span>
                      </div>
                    </div>
                    <span className="text-xl font-mono text-white">{player.total}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Phase Reference */}
        <div className="mt-4 bg-blue-800/30 rounded-xl p-4 border border-blue-700/30">
          <h3 className="text-cyan-300 text-lg font-bold mb-2 text-center">📋 PHASES</h3>
          <div className="grid grid-cols-2 gap-1 text-sm">
            {phaseDescriptions.map((desc, i) => (
              <div
                key={i}
                className={`flex gap-2 p-1 rounded ${
                  currentPhases.some(p => p === i + 1) ? 'bg-cyan-500/20 text-cyan-200' : 'text-blue-300'
                }`}
              >
                <span className="text-cyan-400 font-bold w-4">{i + 1}.</span>
                <span className="text-xs">{desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
